import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
const _excluded = ["endSlot", "Heading", "responsive", "startSlot", "titleText"];
import styled, { css } from 'foundations-theming-specialty/utils';
import { AbstractToolbar } from '../AbstractToolbar/AbstractToolbar';
import { ToolBarContext } from '../ToolBar/ToolBar';
import { toPx } from '../../../utils/toPx';
import { StyledFlex } from '../../../components/Flex/Flex';
import { InnerFlex } from '../AbstractToolbar/InnerFlex';
import { SlotFlex } from '../AbstractToolbar/SlotFlex';
import { StyledIconWrapper, StyledNav } from '../Breadcrumbs/Breadcrumbs';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { setFontSmoothing } from '../../utils/styles/styles';
import { StyledLink } from '../Link/Link';
import { getColorTokensForUse as getLinkColorTokensForUse } from '../Link/Link.styles';
import { Affordance as AutoSizedTextInputAffordance, StyledIconWrapper as AutoSizedTextInputIconWrapper } from '../AutoSizedTextInput/AutoSizedTextInput';
import { MIN_HEIGHT } from '../../../constants/nonTokenisedValues';

/** @internal */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledEditorToolbar = styled(_ref => {
  let rest = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/_jsx(AbstractToolbar, Object.assign({}, rest));
}).withConfig({
  displayName: "EditorToolbar__StyledEditorToolbar",
  componentId: "sc-1y1m3iq-0"
})([""]);
const StyledFlexWrapper = styled.div.withConfig({
  displayName: "EditorToolbar__StyledFlexWrapper",
  componentId: "sc-1y1m3iq-1"
})(({
  $cssStyles,
  theme
}) => {
  const LAYOUT_EDITOR_PADDING = theme.space['500'];
  const BASE_SPACING_X = theme.space['300'];
  const COMPUTED_PADDING_LEFT = toPx(parseInt(LAYOUT_EDITOR_PADDING, 10) + parseInt(BASE_SPACING_X, 10));
  return Object.assign({
    [`> ${StyledFlex}:first-of-type`]: {
      backgroundColor: theme.color['global-shell-fill-surface-default'],
      color: theme.color['text-inverse-default'],
      borderRadius: theme.borderRadius['transitional-component'],
      paddingInlineStart: COMPUTED_PADDING_LEFT,
      paddingInlineEnd: LAYOUT_EDITOR_PADDING,
      marginBlock: theme.space['0'],
      display: 'block',
      paddingBlockStart: theme.space['300'],
      [`> ${StyledFlex}:first-of-type`]: {
        minHeight: MIN_HEIGHT.TOOLBAR,
        margin: `-${theme.space['300']} ${theme.space['0']} ${theme.space['0']} -${theme.space['300']}`,
        columnGap: theme.space['0'],
        inlineSize: 'auto',
        [`> ${StyledFlex}`]: {
          '> * + *': {
            marginInlineStart: theme.space['300']
          },
          padding: `${theme.space['200']} theme.space['0']`,
          columnGap: theme.space['0']
        },
        [`> ${StyledFlex}:not(:first-of-type)`]: {
          marginLeft: theme.space['300']
        }
      }
    },
    '&': {
      [`${StyledNav}`]: {
        verticalAlign: 'bottom',
        // using the size of the text to make sure this is perfectly aligned
        lineHeight: theme.typography['body-100'].fontSize
      },
      [`${StyledNav} > *`]: {
        textDecoration: 'none',
        verticalAlign: 'inherit'
      },
      [`${StyledIconWrapper}`]: {
        fontSize: theme.typography['body-400'].fontSize
      },
      [`& ${StyledLink}`]: {
        fontWeight: theme.typography['heading-50'].fontWeight,
        fontSize: theme.typography['heading-50'].fontSize,
        color: theme.color[getLinkColorTokensForUse('on-dark').idle],
        [`& [data-icon-name]`]: {
          fill: `${theme.color['icon-inverse-default']}!important`,
          transition: 'all 0.1s ease-in'
        },
        '&:hover': {
          color: theme.color[getLinkColorTokensForUse('on-dark').hover],
          textDecoration: 'underline solid',
          [`& [data-icon-name]`]: {
            fill: `${theme.color['icon-inverse-hover']}!important`
          }
        },
        ':focus': {
          // link focus box shadow doesn't play nice with the toolbar
          boxShadow: 'none!important'
        }
      },
      [`& ${AutoSizedTextInputAffordance}`]: {
        color: theme.color['text-inverse-default'],
        '&:hover': {
          backgroundColor: theme.color['global-shell-fill-editortitle-hover']
        }
      },
      [`& ${AutoSizedTextInputIconWrapper}`]: {
        color: theme.color['text-inverse-default']
      }
    }
  }, $cssStyles);
});
export const getEditorHeadingStyles = props => {
  const {
    theme
  } = props;
  const styles = css(["font-size:", ";", ";font-weight:", ";margin-bottom:", ";margin:-5px;padding:5px;max-width:100%;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;"], theme.typography['body-400'].fontSize, setFontSmoothing('auto'), theme.typography['body-400'].fontWeight, theme.space['0']);
  return styles;
};
const EditorHeading = styled(_ref2 => {
  let rest = Object.assign({}, (_objectDestructuringEmpty(_ref2), _ref2));
  return /*#__PURE__*/_jsx("h1", Object.assign({}, rest));
}).withConfig({
  displayName: "EditorToolbar__EditorHeading",
  componentId: "sc-1y1m3iq-2"
})(["", ";"], props => getEditorHeadingStyles(props));
const EditorInner = styled(InnerFlex).withConfig({
  displayName: "EditorToolbar__EditorInner",
  componentId: "sc-1y1m3iq-3"
})([""]);
const EditorMiddle = rest => {
  return /*#__PURE__*/_jsx(SlotFlex, Object.assign({}, rest, {
    position: "middle"
  }));
};
const RenderHeading = ({
  Heading,
  titleText
}) => {
  const Element = Heading;
  return /*#__PURE__*/_jsx(Element, {
    "aria-level": 1,
    children: titleText
  });
};
export const EditorToolbar = props => {
  const {
      endSlot,
      Heading = EditorHeading,
      responsive = false,
      startSlot,
      titleText
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);

  // the only time you should set startSlot to fragment is if there is an endSlot and no startSlot
  const computedStartSlot = !startSlot && titleText && endSlot ? /*#__PURE__*/_jsx(_Fragment, {}) : startSlot;
  const computedEndSlot = !endSlot && titleText && startSlot ? /*#__PURE__*/_jsx(_Fragment, {}) : endSlot;
  const {
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(ToolBarContext.Provider, {
    value: {
      toolBar: true
    },
    children: /*#__PURE__*/_jsx(StyledFlexWrapper, {
      $cssStyles: cssStyles,
      children: /*#__PURE__*/_jsx(StyledEditorToolbar, Object.assign({
        __distributeEvenly: true,
        endSlot: computedEndSlot,
        Inner: EditorInner,
        Middle: EditorMiddle,
        middleSlot: RenderHeading({
          Heading,
          titleText
        }),
        responsive: responsive,
        startSlot: computedStartSlot
      }, rest))
    })
  });
};